import Vue from "vue";
import VueRouter, { RouteConfig } from "vue-router";

import { AuthClientSelectedGuard, AuthGuard, NotLoggedInAuthGuard, TwoFactorAuthGuard } from "@/utils/auth";
import env from "@/utils/env";
import About from "@/views/about/About.vue";
import Credits from "@/views/about/Credits.vue";
import EULA from "@/views/about/EULA.vue";
import SignIn from "@/views/auth/SignIn.vue";
import TwoFactorAuthentication from "@/views/auth/TwoFactorAuthentication.vue";
import ConnectionError from "@/views/errors/ConnectionError.vue";
import Error from "@/views/errors/Error.vue";
import NotFound from "@/views/errors/NotFound.vue";
import Unauthorized from "@/views/errors/Unauthorized.vue";
import MyPatients from "@/views/MyPatients.vue";
import PatientInfusion from "@/views/patient/infusion/PatientInfusion.vue";
import Patient from "@/views/patient/Patient.vue";
import PatientInfo from "@/views/patient/PatientInfo.vue";
import PatientMessages from "@/views/patient/PatientMessages.vue";
import ChangePassword from "@/views/profile/ChangePassword.vue";
import UserProfile from "@/views/profile/UserProfile.vue";
import SelectHospital from "@/views/SelectHospital.vue";
import SelectPatients from "@/views/SelectPatients.vue";

Vue.use(VueRouter);

// FYI For some reason, Vue's beforeEnter nav guards aren't working when assinged directly, so use lambdas to get them
// to work: e.g. "beforeEnter: (to, from, next) => AuthGuard(to, from, next)"
const routes: Array<RouteConfig> = [
  // This is just a redirect to the "Select Patients" page and it won't be tracked by Google Analytics
  {
    path: "/",
    redirect: "/select-patients",
    meta: {
      title: "Home"
    }
  },
  {
    path: "/sign-in",
    name: "Sign In",
    beforeEnter: (to, from, next) => NotLoggedInAuthGuard(to, from, next),
    component: SignIn,
    meta: {
      title: "Sign In"
    }
  },
  {
    path: "/2fa",
    name: "Verification Code",
    beforeEnter: (to, from, next) => TwoFactorAuthGuard(to, from, next),
    component: TwoFactorAuthentication,
    meta: {
      title: "2FA"
    }
  },
  {
    path: "/user-profile",
    name: "User Profile",
    beforeEnter: (to, from, next) => AuthGuard(to, from, next),
    component: UserProfile,
    meta: {
      title: "User Profile"
    }
  },
  {
    path: "/change-password",
    name: "Change Password",
    beforeEnter: (to, from, next) => AuthGuard(to, from, next),
    component: ChangePassword,
    meta: {
      title: "Change Password"
    }
  },
  {
    path: "/about",
    name: "About",
    component: About,
    meta: {
      title: "About"
    }
  },
  {
    path: "/credits",
    name: "Credits",
    component: Credits,
    meta: {
      title: "Credits"
    }
  },
  {
    path: "/eula",
    name: "License Agreement",
    component: EULA,
    meta: {
      title: "License Agreement"
    }
  },

  {
    path: "/select-hospital",
    name: "Select Hospital",
    beforeEnter: (to, from, next) => AuthGuard(to, from, next),
    component: SelectHospital,
    meta: {
      title: "Select Hospital"
    }
  },
  {
    path: "/select-patients",
    name: "Select Patients",
    beforeEnter: (to, from, next) => AuthClientSelectedGuard(to, from, next),
    component: SelectPatients,
    meta: {
      title: "Select Patients"
    }
  },
  {
    path: "/my-patients",
    name: "My Patients",
    beforeEnter: (to, from, next) => AuthClientSelectedGuard(to, from, next),
    component: MyPatients,
    meta: {
      title: "My Patients"
    }
  },
  {
    path: "/patients/:id",
    beforeEnter: (to, from, next) => AuthClientSelectedGuard(to, from, next),
    component: Patient,
    children: [
      {
        name: "Patient Details - Ventilation",
        path: "info",
        component: PatientInfo,
        meta: { title: "Patient Details - Ventilation" }
      },
      {
        name: "Patient Details - Infusion",
        path: "infusion",
        component: PatientInfusion,
        meta: { title: "Patient Details - Infusion" }
      },
      {
        name: "Patient Details - Messages",
        path: "messages",
        component: PatientMessages,
        meta: { title: "Patient Details - Messages" }
      },
      // This is just a redirect to the child "info" page and it won't be tracked by Google Analytics
      { name: "Patient Details", path: "", redirect: "info" }
    ],
    meta: {
      title: "Patient Details"
    }
  },
  {
    path: "/unauthorized",
    name: "Unauthorized",
    component: Unauthorized,
    meta: {
      title: "Unauthorized"
    }
  },
  {
    path: "/error",
    name: "Error",
    component: Error,
    meta: {
      title: "Error"
    }
  },
  {
    path: "/connection-error",
    name: "Connection Error",
    component: ConnectionError,
    meta: {
      title: "Connection Error"
    }
  },

  // FYI this route must be last in order to catch any unmatched requests
  {
    path: "*",
    name: "Not Found",
    component: NotFound,
    meta: {
      title: "Not Found"
    }
  }
];

const router = new VueRouter({
  mode: "history",
  base: env.baseUrl,
  routes
});

router.afterEach(to => {
  Vue.nextTick(() => {
    document.title = to.meta.title;
  });
});

export default router;
